import { createContext, useContext, useEffect, useState } from "react";
import type { Me } from "../api/types";
import { ApiError } from "../api/client";
import { getMe } from "../api/auth";
import { SESSION_KEY } from "./sessionKey";

// ────────────────────────────────────────────────────────────────
// Auth state lifecycle:
//   loading → ready    (happy path)
//   loading → error    (SSO failed, network down, popup blocked…)
//
// Performance: two paths on mount.
//   1. WARM — sessionStorage has an OpenWatch JWT. Skip MSAL
//      entirely, just call getMe() to validate. ~200ms.
//   2. COLD — no JWT. Dynamic-import the heavy auth modules and
//      run the full SSO flow. ~3-15s depending on network and
//      whether the user must interactively sign in.
//
// While loading, render a React loader visually identical to the
// inline HTML loader in index.html — so the swap from HTML to React
// is invisible.
// ────────────────────────────────────────────────────────────────

type AuthState =
  | { status: "loading" }
  | { status: "ready"; user: Me }
  | { status: "error"; error: unknown };

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;

    async function go() {
      // ── Warm path ────────────────────────────────────────────
      // If we already have a session JWT from a previous visit, try
      // it. getMe() returns 401 if expired — we fall through to the
      // cold path in that case.
      const existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) {
        try {
          const user = await getMe();
          if (!cancelled) setState({ status: "ready", user });
          return;
        } catch (e) {
          // Only retry the cold path on actual auth failure.
          // Network errors etc. should surface as errors directly.
          const isAuthFailure =
            e instanceof ApiError && (e.status === 401 || e.status === 403);
          if (!isAuthFailure) {
            if (!cancelled) setState({ status: "error", error: e });
            return;
          }
          // 401 → fall through to cold path
          sessionStorage.removeItem(SESSION_KEY);
        }
      }

      // ── Cold path ────────────────────────────────────────────
      // Dynamic import keeps MSAL + Teams SDK (~90 KB) out of the
      // initial bundle. They download in parallel with React's first
      // paint — and on the warm path, never at all.
      try {
        const { runAuthFlow } = await import("./runAuthFlow");
        const { user } = await runAuthFlow();
        if (!cancelled) setState({ status: "ready", user });
      } catch (error) {
        if (!cancelled) setState({ status: "error", error });
      }
    }

    go();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") return <AuthLoader />;
  if (state.status === "error") return <AuthError error={state.error} />;

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): { user: Me } {
  const ctx = useContext(AuthContext);
  if (!ctx || ctx.status !== "ready") {
    throw new Error(
      "useAuth() called outside an authenticated context. Wrap the tree in <AuthProvider>.",
    );
  }
  return { user: ctx.user };
}

function AuthLoader() {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center gap-3"
      style={{ background: "#fafbfc", color: "#8a94a8", font: "500 13px Inter, system-ui, sans-serif" }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: "2px solid rgba(124, 58, 237, 0.18)",
          borderTopColor: "#7c3aed",
          animation: "ow-spin 0.8s linear infinite",
        }}
      />
      <span>Signing you in…</span>
    </div>
  );
}

function AuthError({ error }: { error: unknown }) {
  const message =
    error instanceof Error ? error.message : "Unknown error during sign-in";

  // A plain reload often lands on the same failure: the stale JWT in
  // sessionStorage triggers the warm path (which 401s), MSAL's cached
  // interaction state may still be "in progress", etc. Wipe the auth
  // surface area first so the next mount runs a clean cold-path flow.
  const retry = () => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
      // MSAL keeps cache keys under sessionStorage (our config default).
      for (const k of Object.keys(sessionStorage)) {
        if (k.startsWith("msal.") || k.includes("login.windows.net") || k.includes("login.microsoftonline.com")) {
          sessionStorage.removeItem(k);
        }
      }
    } catch {
      // Best-effort cleanup; reload anyway.
    }
    window.location.reload();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: "#fafbfc" }}
    >
      <div className="max-w-[380px] text-center px-6">
        <div className="text-[18px] font-semibold text-fg-strong mb-2">
          Could not sign you in
        </div>
        <div className="text-fg-soft mb-5 text-[13px]">{message}</div>
        <button className="btn btn-primary" onClick={retry}>
          Try again
        </button>
      </div>
    </div>
  );
}
